import { Suspense, type ReactElement } from "react";
import { createHashRouter } from "react-router-dom";
import FieldSetupMobileLayout from "../../layouts/FieldSetupMobile";
import { routes } from "./routes";
import { PageSizeLoader } from "../../components/CustomComponents/PageSizeLoader";
import { AppErrorBoundary } from "../../components/CustomComponents/PageErrorBoundry";
import { ErrorComponent } from "../../components/CustomComponents/PageErrorBoundry/ErrorComponent";
import { RequireAuth, RequireStep } from "../guards";

const ROUTE_GUARD_CONFIG: Record<
  string,
  { requireAuth?: boolean; requireStep?: number }
> = {
  login: { requireAuth: false },
  catchAll: { requireAuth: false },
  /** Device (route /2): requires account step */
  setupStep2: { requireStep: 1 },
  /** Verify (route /3): requires device step */
  setupStep3: { requireStep: 2 },
  /** Handover (route /4): requires verify step */
  setupStep4: { requireStep: 3 },
  /** Complete: requires handover step */
  setupComplete: { requireStep: 4 },
};

function wrapRouteContent(routeKey: string, page: ReactElement) {
  const cfg = ROUTE_GUARD_CONFIG[routeKey] ?? {};
  let wrapped: ReactElement = page;
  if (cfg.requireStep !== undefined) {
    wrapped = (
      <RequireStep requiredStep={cfg.requireStep}>{wrapped}</RequireStep>
    );
  }
  if (cfg.requireAuth !== false) {
    wrapped = <RequireAuth>{wrapped}</RequireAuth>;
  }
  return wrapped;
}

export const mobileRouter = createHashRouter([
  {
    path: "/",
    element: <FieldSetupMobileLayout />,
    children: Object.entries(routes).map(([routeKey, route]) => ({
      ...route,
      element: (
        <AppErrorBoundary
          FallbackComponent={({ error, resetErrorBoundary }) => (
            <ErrorComponent
              error={error}
              resetErrorBoundary={resetErrorBoundary}
            />
          )}
          resetKeys={[routeKey]}
        >
          <Suspense
            fallback={
              <PageSizeLoader
                loader={
                  <div
                    className="flex items-center justify-center p-8"
                    aria-busy="true"
                  >
                    <div
                      className="h-12 w-12 animate-spin rounded-full border-4 border-brand-navy border-t-transparent"
                      role="presentation"
                    />
                  </div>
                }
              />
            }
          >
            {wrapRouteContent(routeKey, <route.element />)}
          </Suspense>
        </AppErrorBoundary>
      ),
    })),
  },
]);
